'use client';
import React, { useState, useEffect, useRef, useMemo, useCallback } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Box,
  IconButton
} from '@mui/material';
import SnackBar from 'src/components/era-package/era/SnackBar';
import RenderFormSection from 'src/components/era-package/forms/RenderFormSection';
import Button from 'src/components/era-package/forms/Button';
import Buttons from 'src/components/era-package/era/Buttons';
import { useUnsavedAlert } from 'src/utils/era-package/setupUnsavedAlert';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {faClose, faFloppyDisk as saveIcon, faPlus, faSpinnerThird} from '@fortawesome/pro-regular-svg-icons';
import { useFieldArray, useFormContext, useWatch } from 'react-hook-form';
import { addTabKeyToOptions } from 'src/utils/era-package/tab-utils';
import AbiTradePartnerApiUrls from '../../../apiUrls/AbiTradePartnerApiUrls';
import { FileForUserGetPartyId } from '../../../common/FileForUserCommon';
import TradePartnerLookupDialog from '../../../views/abi/component/TradePartnerLookupDialog';

const EditModal = ({
  title = 'Record',
  open,
  onClose,
  onSave,
  onSaveData,
  onDelete,
  fields, name,
  optionsMap,
  initialData,
  isEditing, index,
  handleRefreshData,
}) => {
  const [formData, setFormData] = useState({});
  const [errors, setErrors] = useState({});
  const [hasUnsaved, setHasUnsaved] = useState(false);
  const rhfArray = useFieldArray({ name }) || null
  const rhf = useFormContext() || null
  const [resetData, setResetData] = useState(null) // need to cache this data; formData & initialData will change

    const [showErrorSnackbar, setShowErrorSnackbar] = useState(false);
  const [showSaveErrorSnackbar, setShowSaveErrorSnackbar] = useState(false);
  const [showSuccessSnackbar, setShowSuccessSnackbar] = useState(false);
  const [showTradePartnerSuccessSnackbar, setShowTradePartnerSuccessSnackbar] = useState(false);
  const [showTradePartnerErrorSnackbar, setShowTradePartnerErrorSnackbar] = useState(false);
      const [loading, setLoading] = useState(false);
  const [partyLookupOptions, setPartyLookupOptions] = useState([]);
  const [partyLookupLoaded, setPartyLookupLoaded] = useState(false);
  const [lookupDialogOpen, setLookupDialogOpen] = useState(false);
  const [lookupSearchText, setLookupSearchText] = useState('');

  const fieldIndex = isEditing ? index : rhfArray?.fields?.length
  const indexedName = `${name}.${fieldIndex}`

              // Check if this is the Parties modal (cargoEntityList or partyContactList)
  const isPartiesModal = name?.includes('cargoEntityList') || name?.includes('partyContactList')
  const isFdaPartiesModal = name?.includes('partyContactList')
  
  // Debug logging
  useEffect(() => {
    if (open) {
      console.log('=== EditModal OPENED ===')
      console.log('name prop:', name)
      console.log('indexedName:', indexedName)
      console.log('isPartiesModal:', isPartiesModal)
      console.log('fields prop:', fields)
      console.log('fields names:', fields?.map(f => f.name))
    }
  }, [open, name, indexedName, isPartiesModal, fields])

  // Watch partyLookup field for auto-population
  const partyLookupValue = useWatch({
    control: rhf?.control,
    name: `${indexedName}.partyLookup`
  })

    // Fetch party lookup options when modal opens for Parties
  useEffect(() => {
    if (open && isPartiesModal && !partyLookupLoaded) {
      const loadPartyLookupOptions = async () => {
        try {
          // Fetch trade partners directly from API
          const response = await fetch(
            AbiTradePartnerApiUrls.GET + `?partyId=${FileForUserGetPartyId()}`,
            {
              method: 'GET',
              headers: {
                'Content-Type': 'application/json',
                Authorization: 'Bearer ' + JSON.parse(localStorage.getItem('userToken'))
              }
            }
          )
          
          if (response.ok) {
            const json = await response.json()
            const partners = json?.data || []
            // Map to format needed for autocomplete
            const options = partners.map(partner => ({
              value: partner.id?.toString() || partner.partyMasterName,
              partyMasterName: partner.partyMasterName,
              name: partner.name,
              description: partner.partyMasterName || partner.name,
              ...partner
            }))
            setPartyLookupOptions(options)
            setPartyLookupLoaded(true)
          } else {
            console.error('Error fetching trade partners')
            setPartyLookupOptions([])
          }
        } catch (error) {
          console.error('Error loading party lookup options:', error)
          setPartyLookupOptions([])
        }
      }
      loadPartyLookupOptions()
    }
  }, [open, isPartiesModal, partyLookupLoaded])

  // Reset partyLookupLoaded when modal closes
  useEffect(() => {
    if (!open) {
      setPartyLookupLoaded(false)
    }
  }, [open])

              // Auto-populate fields when partyLookup changes
  useEffect(() => {
    if (!partyLookupValue || !isPartiesModal || !rhf || partyLookupOptions.length === 0) return

    // Find the selected party from options
    const selectedParty = partyLookupOptions.find(opt => opt.value === partyLookupValue)
    
    if (selectedParty?.data) {
      const data = selectedParty.data
      
      if (isFdaPartiesModal) {
        // FDA Parties & Point of Contact field mapping
        rhf.setValue(`${indexedName}.entityName`, data.name || '', { shouldDirty: true })
        rhf.setValue(`${indexedName}.entityNumber`, data.identifier || '', { shouldDirty: true })
        rhf.setValue(`${indexedName}.entityAddress1`, data.address1 || '', { shouldDirty: true })
        rhf.setValue(`${indexedName}.entityAddress2`, data.address2 || '', { shouldDirty: true })
        rhf.setValue(`${indexedName}.city`, data.city || '', { shouldDirty: true })
        rhf.setValue(`${indexedName}.stateProvince`, data.stateProvince || '', { shouldDirty: true })
        rhf.setValue(`${indexedName}.postalCode`, data.postalCode || '', { shouldDirty: true })
        rhf.setValue(`${indexedName}.country`, data.country || '', { shouldDirty: true })
      } else {
        // Line Item Parties (cargoEntityList) field mapping
        rhf.setValue(`${indexedName}.partyCode`, data.partyCode || '', { shouldDirty: true })
        rhf.setValue(`${indexedName}.name`, data.name || '', { shouldDirty: true })
        rhf.setValue(`${indexedName}.identifier`, data.identifier || '', { shouldDirty: true })
        rhf.setValue(`${indexedName}.duns`, data.duns || '', { shouldDirty: true })
        rhf.setValue(`${indexedName}.address1`, data.address1 || '', { shouldDirty: true })
        rhf.setValue(`${indexedName}.address2`, data.address2 || '', { shouldDirty: true })
        rhf.setValue(`${indexedName}.city`, data.city || '', { shouldDirty: true })
        rhf.setValue(`${indexedName}.stateProvince`, data.stateProvince || '', { shouldDirty: true })
        rhf.setValue(`${indexedName}.postalCode`, data.postalCode || '', { shouldDirty: true })
        rhf.setValue(`${indexedName}.country`, data.country || '', { shouldDirty: true })
      }
      
      setHasUnsaved(true)
    }
  }, [partyLookupValue, isFdaPartiesModal])

  // Refs for focus management
  const dialogRef = useRef(null);

  // Set up unsaved alert functionality
  useUnsavedAlert(hasUnsaved);

    const modalFields = useMemo(() => {
    return !!rhfArray && Array.isArray(fields)
      ? fields.map(field => {
        let copy = { ...field }
        copy.name = `${indexedName}.${copy.name}`
                        // Add lookup click handler for lookupButton type fields
        if (field.type === 'lookupButton' && isPartiesModal) {
          copy.onLookupClick = () => {
            setLookupDialogOpen(true)
          }
          copy.onLookupSelect = (partner) => {
            handleTradePartnerSelect(partner)
          }
        }
        return copy
      })
      : fields
  }, [fields, indexedName, rhfArray, isPartiesModal])

      const indexedOptions = useMemo(() => {
    // Build the options map with partyLookup options if available
    const baseOptions = isPartiesModal 
      ? { ...optionsMap, partyLookup: partyLookupOptions }
      : optionsMap
    return !!rhfArray
      ? addTabKeyToOptions(baseOptions, indexedName)
      : baseOptions
  }, [optionsMap, partyLookupOptions, indexedName, rhfArray, isPartiesModal])

  // Comprehensive focus management to prevent aria-hidden conflicts
  useEffect(() => {
    if (open) {

      // Immediately blur all focused elements in main wrapper when modal opens
      const mainWrapper = document.querySelector('.mainwrapper');
      if (mainWrapper) {
        const allFocusedElements = mainWrapper.querySelectorAll(':focus');
        allFocusedElements.forEach(element => {
          element.blur();
        });

        // Also blur any buttons that might retain focus
        const allButtons = mainWrapper.querySelectorAll('button, [role="button"]');
        allButtons.forEach(button => {
          if (document.activeElement === button) {
            button.blur();
          }
        });
      }

      // Set up continuous monitoring to catch any focus that slips through
      const focusMonitor = setInterval(() => {
        const mainWrapper = document.querySelector('.mainwrapper');
        const focusedElement = document.activeElement;

        if (mainWrapper &&
          mainWrapper.getAttribute('aria-hidden') === 'true' &&
          focusedElement &&
          mainWrapper.contains(focusedElement)) {

          // Immediately blur the problematic element
          focusedElement.blur();

          // Force focus to modal
          const dialog = dialogRef.current;
          if (dialog) {
            const firstFocusable = dialog.querySelector(
              'button, [href], input, select, textarea, [tabindex]:not([tabindex="-1"])'
            );
            if (firstFocusable) {
              firstFocusable.focus();
            }
          }
        }
      }, 50); // More frequent monitoring

      return () => {
        clearInterval(focusMonitor);
      };
    }
  }, [open]);

  useEffect(() => {
    if (initialData) {
      setFormData(initialData);
      setResetData({ ...initialData })
    } else {
      // Initialize with default values
      const defaults = {};
      fields.forEach(field => {
        defaults[field.name] = field.defaultValue;
      });
      setFormData(defaults);
    }
    // Reset unsaved state when modal opens/closes or initial data changes
    setHasUnsaved(false);
  }, [initialData, fields, open]);

    const validateForm = async () => {
    const newErrors = {};

    if (!!rhf) {
      if (rhf.getValues(indexedName) !== undefined) {
        // Additional validation for conditional required fields
        if (isPartiesModal) {
          const currentData = rhf.getValues(indexedName)
          if (currentData?.saveTradePartner && !currentData?.partyMasterName?.trim()) {
            rhf.setError(`${indexedName}.partyMasterName`, {
              type: 'required',
              message: 'Profile Name is required when saving to Trade Partner'
            })
            return false
          }
        }
        const err = await rhf.trigger(indexedName)
        return err
      } else {
        // item is empty.
        setShowErrorSnackbar(true)
        return false
      }
    }

    // no RHF. Manual validation
    modalFields.forEach(field => {
      // Check for conditional required (requiredCondition)
      let isRequired = field.required || false;
      if (field.requiredCondition && typeof field.requiredCondition === 'function') {
        isRequired = field.requiredCondition(formData, formData);
      }
      
      if (isRequired) {
        const value = formData[field.name];
        if (!value || String(value).trim() === '') {
          newErrors[field.name] = `${field.label} is required`;
        }
      }
    });

    setErrors(newErrors);
    console.log('errors', newErrors)
    return Object.keys(newErrors).length === 0;
  };

            // Function to save party to Trade Partner
  const saveToTradePartner = async (partyData) => {
    try {
      const tradePartnerPayload = {
        partyId: FileForUserGetPartyId(),
        partyMasterName: partyData.profileName,
        partyCode: partyData.partyCode,
        name: partyData.name,
        taxIdNumber: partyData.identifier,
        partyIdentifier: partyData.identifier,
        duns: partyData.duns,
        address1: partyData.address1,
        address2: partyData.address2,
        city: partyData.city,
        stateProvince: partyData.stateProvince,
        postalCode: partyData.postalCode,
        countryCode: partyData.country,
        active: true
      }

      const response = await fetch(AbiTradePartnerApiUrls.POST, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: 'Bearer ' + JSON.parse(localStorage.getItem('userToken'))
        },
        body: JSON.stringify(tradePartnerPayload)
      })

            console.log('Response status:', response.status)
      console.log('Response ok:', response.ok)

      if (!response.ok) {
        const errorText = await response.text()
        console.error('Error saving to Trade Partner - Status:', response.status)
        console.error('Error response body:', errorText)
        console.log('=== SAVE TO TRADE PARTNER FAILED ===')
        return false
      }

      const result = await response.json()
      console.log('Trade Partner saved successfully:', result)
      console.log('=== SAVE TO TRADE PARTNER SUCCESS ===')
      return true
    } catch (error) {
      console.error('Error saving to Trade Partner:', error)
      console.error('Error stack:', error.stack)
      console.log('=== SAVE TO TRADE PARTNER ERROR ===')
      return false
    }
  }

        const handleSave = async () => {
    console.log('=== HANDLE SAVE TRIGGERED ===')
    let isValid = await validateForm()
    console.log('Form validation result:', isValid)
    if (isValid) {
      setLoading(true)
      setHasUnsaved(false); // Clear unsaved flag on successful save
            let toSend = !!rhf ? rhf.getValues(indexedName) : formData
      
  
      
  
      let result = true
      if (typeof onSaveData === 'function') {
        result = await onSaveData(toSend, rhf);
      }

      if(result)
        setShowSuccessSnackbar(true)
      else
        setShowSaveErrorSnackbar(true)

      setLoading(false)
      onSave(toSend, rhfArray);
    }
  };

  const handleClose = () => {
    if (hasUnsaved) {
      const message = 'You have unsaved changes. Are you sure you want to close without saving?';
      if (window.confirm(message)) {
        setHasUnsaved(false);
        if (!!rhfArray) {
          if (isEditing) // reset data for this index
            rhfArray.update(fieldIndex, resetData)
          else
            rhfArray.remove(fieldIndex)
        }
        onClose();
      }
    } else {
      onClose();
    }
  };

    // Handle keyboard navigation for accessibility
  const handleKeyDown = (event) => {
    if (event.key === 'Escape') {
      handleClose();
    } else if (event.key === 'Tab') {
      // Ensure focus stays within the modal
      const dialog = dialogRef.current;
      if (dialog) {
        const focusableElements = dialog.querySelectorAll(
          'button, [href], input, select, textarea, [tabindex]:not([tabindex="-1"])'
        );
        const firstElement = focusableElements[0];
        const lastElement = focusableElements[focusableElements.length - 1];

        if (event.shiftKey) {
          // Shift + Tab
          if (document.activeElement === firstElement) {
            event.preventDefault();
            lastElement.focus();
          }
        } else {
          // Tab
          if (document.activeElement === lastElement) {
            event.preventDefault();
            firstElement.focus();
          }
        }
      }
    }
  };

      // Handle trade partner selection from lookup dialog
  const handleTradePartnerSelect = (partner) => {
    if (!partner || !rhf) return
    
    console.log('Trade Partner selected:', partner)
    
    if (isFdaPartiesModal) {
      // FDA Parties & Point of Contact field mapping
      rhf.setValue(`${indexedName}.partyMasterName`, partner.partyMasterName || '', { shouldDirty: true })
      rhf.setValue(`${indexedName}.entityName`, partner.name || '', { shouldDirty: true })
      rhf.setValue(`${indexedName}.entityNumber`, partner.partyIdentifier || '', { shouldDirty: true })
      rhf.setValue(`${indexedName}.entityAddress1`, partner.address1 || '', { shouldDirty: true })
      rhf.setValue(`${indexedName}.entityAddress2`, partner.address2 || '', { shouldDirty: true })
      rhf.setValue(`${indexedName}.city`, partner.city || '', { shouldDirty: true })
      rhf.setValue(`${indexedName}.stateProvince`, partner.stateProvince || '', { shouldDirty: true })
      rhf.setValue(`${indexedName}.postalCode`, partner.postalCode || '', { shouldDirty: true })
      rhf.setValue(`${indexedName}.country`, partner.countryCode || '', { shouldDirty: true })
    } else {
      // Line Item Parties (cargoEntityList) field mapping
      rhf.setValue(`${indexedName}.partyMasterName`, partner.partyMasterName || '', { shouldDirty: true })
      rhf.setValue(`${indexedName}.partyCode`, partner.partyCode || '', { shouldDirty: true })
      rhf.setValue(`${indexedName}.name`, partner.name || '', { shouldDirty: true })
      rhf.setValue(`${indexedName}.identifier`, partner.partyIdentifier || '', { shouldDirty: true })
      rhf.setValue(`${indexedName}.duns`, partner.duns || '', { shouldDirty: true })
      rhf.setValue(`${indexedName}.address1`, partner.address1 || '', { shouldDirty: true })
      rhf.setValue(`${indexedName}.address2`, partner.address2 || '', { shouldDirty: true })
      rhf.setValue(`${indexedName}.city`, partner.city || '', { shouldDirty: true })
      rhf.setValue(`${indexedName}.stateProvince`, partner.stateProvince || '', { shouldDirty: true })
      rhf.setValue(`${indexedName}.postalCode`, partner.postalCode || '', { shouldDirty: true })
      rhf.setValue(`${indexedName}.country`, partner.countryCode || '', { shouldDirty: true })
    }
    
    setHasUnsaved(true)
  }

    const formSection = {
    title: 'Details',
    fields: modalFields,
    onLookupButtonClick: isPartiesModal ? () => setLookupDialogOpen(true) : undefined,
  };

  return (<>
    {/* Error Snackbar */}
    <SnackBar
      title="Missing Data"
      type="error"
      open={showErrorSnackbar}
      onClose={() => setShowErrorSnackbar(false)}
      duration={10000}
    >
      Nothing to add.
    </SnackBar>

    {/* Error Snackbar */}
    <SnackBar
      title="Error"
      type="error"
      open={showSaveErrorSnackbar}
      onClose={() => setShowSaveErrorSnackbar(false)}
      duration={10000}
    >
      Internal error occured, please try again later.
    </SnackBar>

    {/* Trade Partner Success Snackbar */}
    <SnackBar
      title="Trade Partner Saved"
      type="success"
      open={showTradePartnerSuccessSnackbar}
      onClose={() => setShowTradePartnerSuccessSnackbar(false)}
      duration={5000}
    >
      Party has been saved to Trade Partner successfully.
    </SnackBar>

    {/* Trade Partner Error Snackbar */}
    <SnackBar
      title="Trade Partner Error"
      type="error"
      open={showTradePartnerErrorSnackbar}
      onClose={() => setShowTradePartnerErrorSnackbar(false)}
      duration={10000}
    >
      Failed to save party to Trade Partner. Please try again.
    </SnackBar>

    {/* Success Snackbar */}
    <SnackBar
      title="Saved successfully"
      type="success"
      open={showSuccessSnackbar}
      onClose={() => setShowSuccessSnackbar(false)}
      duration={10000}
    >
      Data saved successfully
    </SnackBar>

    <Dialog
      open={open}
      onClose={handleClose}
      maxWidth="100dvw"
      fullWidth
      ref={dialogRef}
      PaperProps={{
        sx: {
          maxHeight: '90vh',
        },
      }}
      TransitionProps={{
        onEntered: () => {
          // Comprehensive cleanup of any remaining focused elements in main wrapper
          const mainWrapper = document.querySelector('.mainwrapper');
          if (mainWrapper) {
            // Find and blur any focused elements
            const focusedElements = mainWrapper.querySelectorAll(':focus');
            focusedElements.forEach(element => {
              element.blur();
            });

            // Extra safety: blur any MUI buttons that might be retaining focus
            const muiButtons = mainWrapper.querySelectorAll('.MuiButton-root, .MuiButtonBase-root');
            muiButtons.forEach(button => {
              if (document.activeElement === button) {
                button.blur();
              }
            });
          }

          // Ensure focus is properly set in the modal
          setTimeout(() => {
            const dialog = dialogRef.current;
            if (dialog) {
              const firstFocusable = dialog.querySelector(
                'button, [href], input, select, textarea, [tabindex]:not([tabindex="-1"])'
              );
              if (firstFocusable) {
                firstFocusable.focus();
              }
            }
          }, 10);
        }
      }}
      // Ensure proper focus containment
      disableEnforceFocus={false}
      disableAutoFocus={false}
      disableRestoreFocus={false}
      onKeyDown={handleKeyDown}
      aria-labelledby="modal-title"
      aria-describedby="modal-description"
      role="dialog"
    >
      <DialogTitle
        id="modal-title"
        sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}
      >
        {`${isEditing ? 'Edit' : 'Add'} ${title}`}
        <IconButton
          aria-label={`Close ${isEditing ? 'edit' : 'add'} ${title} modal`}
          onClick={handleClose}
          sx={{
            color: (theme) => theme.palette.grey[500],
          }}
          title="Close modal"
        >
          <Box component={FontAwesomeIcon} icon={faClose} sx={{ width: '1em' }} />
        </IconButton>
      </DialogTitle>

      <DialogContent dividers id="modal-description">
        <Box sx={{ py: 2 }}>
          <RenderFormSection
            section={formSection}
            optionsMap={indexedOptions}
            formData={formData}
            setFormData={setFormData}
            errors={errors}
            arrayKey={indexedName}
            setErrors={setErrors}
            setHasUnsaved={setHasUnsaved}
            globalAccordionAction={null} // Not needed for modal
          />
        </Box>
      </DialogContent>

      <DialogActions sx={{ pt: 3 }}>
        <Buttons
          advance={
            <Button
              onClick={handleSave}
              variant="contained"
              title={isEditing ? 'Save changes' : 'Add this record'}
              disabled={loading}
              startIcon={<FontAwesomeIcon icon={loading ? faSpinnerThird : saveIcon} width={16} spin={loading} />}
            >
              {isEditing ? 'Save' : 'Add'}
            </Button>
          }
          other={
            <>
              <Button variant="outlined" onClick={handleClose} title="Go back">
                Cancel
              </Button>
              {isEditing && (
                <Button
                  variant="outlined"
                  onClick={onDelete}
                  color="error"
                  title="Proceed with Caution: Delete record"
                >
                  Delete
                </Button>
              )}
            </>
          }
        />
      </DialogActions>
    </Dialog>

    {/* Trade Partner Lookup Dialog */}
    {isPartiesModal && (
                  <TradePartnerLookupDialog
        open={lookupDialogOpen}
        onClose={() => setLookupDialogOpen(false)}
        onSelect={handleTradePartnerSelect}
        title="Trade Partner List"
        initialSearchText={lookupSearchText}
      />
    )}
  </>);
};

export default EditModal;
